import { IconChip } from "@/assets/icons/geist/IconChip";
import { IconPlusSquare } from "@/assets/icons/geist/IconPlusSquare";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { EnvironmentClassSwitch } from "@/components/runners/EnvironmentClassSwitch";
import { Banner } from "@/components/flexkit/Banner";
import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { EnvironmentClassAddModal } from "@/components/runners/details/EnvironmentClassAddModal";
import { EnvironmentClassEditModal } from "@/components/runners/details/EnvironmentClassEditModal";

import {
    RunnerDetailsSection,
    type RunnerDetailsSectionProps,
    type SectionStatus,
} from "@/components/runners/details/RunnerDetailsSection";
import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { vCPUbyInstanceType } from "@/components/instance-types";
import type { TrackLocation } from "@/hooks/use-segment";
import { useListRunnerEnvironmentClasses } from "@/queries/runner-configuration-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { RunnerConfigurationSchema_Field } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type EnvironmentClass, RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, useMemo, useState, type FC } from "react";

/**
 * Sort environment classes by CPU count from instance type.
 * Falls back to alphabetical sorting by display name if no CPU info is available.
 */
function sortEnvironmentClasses(environmentClasses: EnvironmentClass[]): EnvironmentClass[] {
    const getCPU = (environmentClass: EnvironmentClass) => {
        const instanceType = environmentClass.configuration.find((c) => c.key === "instanceType")?.value;
        return instanceType ? vCPUbyInstanceType[instanceType] : undefined;
    };

    return [...environmentClasses].sort((a, b) => {
        // Sort by CPU from instance type
        const aCPU = getCPU(a);
        const bCPU = getCPU(b);
        if (aCPU && bCPU) {
            return aCPU - bCPU;
        }

        // If only one has CPU info, prioritize it
        if (aCPU && !bCPU) {
            return -1;
        }
        if (!aCPU && bCPU) {
            return 1;
        }

        // Fallback to alphabetical sorting by display name
        return (a.displayName || "").localeCompare(b.displayName || "");
    });
}

type ModalState =
    | { type: "closed" }
    | { type: "add" }
    | {
          type: "edit";
          environmentClass: EnvironmentClass;
      };

export const EnvironmentClassesSection: FC<{
    runner: PlainMessage<Runner>;
    schema?: RunnerConfigurationSchema_Field[];
    hasSignedUp: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, schema, hasSignedUp, "data-track-location": dataTrackLocation }) => {
    return (
        <EnvironmentClasses
            runner={runner}
            schema={schema}
            hasSignedUp={hasSignedUp}
            Container={RunnerDetailsSection}
            withDescription={true}
            data-track-location={dataTrackLocation}
        />
    );
};

export const EnvironmentClasses: FC<{
    runner: PlainMessage<Runner>;
    schema?: RunnerConfigurationSchema_Field[];
    hasSignedUp: boolean;
    Container: React.ComponentType<RunnerDetailsSectionProps>;
    withDescription: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, schema, hasSignedUp, Container, withDescription, "data-track-location": dataTrackLocation }) => {
    const {
        data: environmentClasses,
        isLoading: isLoadingEnvironmentClasses,
        error,
    } = useListRunnerEnvironmentClasses(runner.runnerId);

    // Sort environment classes by core count for better UX
    const sortedEnvironmentClasses = useMemo(() => {
        return environmentClasses ? sortEnvironmentClasses(environmentClasses) : [];
    }, [environmentClasses]);

    const { isPending: isLoadingMembership } = useMembership();
    const { hasPermission } = useResourcePermissions({ enabled: true });
    const isLoading = isLoadingEnvironmentClasses || isLoadingMembership;

    const [modalState, setModalState] = useState<ModalState>({ type: "closed" });

    const onCloseModal = (open: boolean) => {
        if (!open) {
            setModalState({ type: "closed" });
        }
    };

    const onEdit = useCallback(
        (environmentClass: EnvironmentClass) => {
            setModalState({
                type: "edit",
                environmentClass,
            });
        },
        [setModalState],
    );

    const onAddEnvironmentClass = useCallback(() => {
        setModalState({ type: "add" });
    }, []);

    const isManagedRunner = runner.provider === RunnerProvider.MANAGED;
    let status: SectionStatus = "loading";
    if (!isLoading) {
        if (!hasSignedUp) {
            status = "locked";
        } else if (!schema?.length) {
            status = "initializing";
        } else {
            status = "optional";
        }
    }

    const ready = status !== "locked" && status !== "initializing";

    const canCreateEnvClass = hasPermission(ResourceType.RUNNER, runner.runnerId, "environmentclass:create");
    const canUpdateEnvClass = hasPermission(ResourceType.RUNNER, runner.runnerId, "environmentclass:update");

    const readOnly = !ready || !canUpdateEnvClass || isManagedRunner;

    return (
        <Container
            status={status}
            title={withDescription ? "Customize your environment classes" : undefined}
            hideStatus={isManagedRunner}
        >
            <SkeletonBlock ready={status !== "loading"} className="mt-1 h-[100px]">
                <div className="flex flex-col gap-4">
                    {withDescription && (
                        <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
                            <Text className={cn(!ready && "text-content-secondary", "text-base")}>
                                Customize your environment&#x2019;s classes to match your team&#x2019;s needs.
                            </Text>
                        </div>
                    )}
                    <ErrorMessage error={error} />
                    <EnvironmentClassesList
                        ready={ready}
                        environmentClassEntries={sortedEnvironmentClasses}
                        onEdit={onEdit}
                        readOnly={readOnly}
                        canAddEnvironmentClass={canCreateEnvClass && !isManagedRunner}
                        onAddEnvironmentClass={onAddEnvironmentClass}
                        isManagedRunner={isManagedRunner}
                    />
                </div>
            </SkeletonBlock>
            {schema && modalState.type !== "closed" && (
                <Dialog open onOpenChange={onCloseModal}>
                    <DialogContent className="max-w-xl" data-track-location={dataTrackLocation(modalState)}>
                        {modalState.type == "edit" && (
                            <EnvironmentClassEditModal
                                environmentClass={modalState.environmentClass}
                                schema={schema}
                                onClose={() => onCloseModal(false)}
                            />
                        )}
                        {modalState.type == "add" && (
                            <EnvironmentClassAddModal
                                runnerId={runner.runnerId}
                                schema={schema}
                                onClose={() => onCloseModal(false)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </Container>
    );
};

const EnvironmentClassesList: FC<{
    ready: boolean;
    environmentClassEntries: EnvironmentClass[];
    readOnly: boolean;
    canAddEnvironmentClass: boolean;
    onEdit: (environmentClass: EnvironmentClass) => void;
    onAddEnvironmentClass: () => void;
    isManagedRunner: boolean;
}> = ({ ready, environmentClassEntries, readOnly, canAddEnvironmentClass, onEdit, onAddEnvironmentClass, isManagedRunner }) => {
    return (
        <div className="flex w-full flex-col gap-2">
            {environmentClassEntries.map((environmentClass) => (
                <EnvironmentClassRow
                    key={environmentClass.id}
                    environmentClass={environmentClass}
                    onEdit={onEdit}
                    readOnly={readOnly}
                    isManagedRunner={isManagedRunner}
                />
            ))}
            {ready && (
                <Button
                    onClick={onAddEnvironmentClass}
                    variant="link"
                    size="lg"
                    disabled={readOnly || !canAddEnvironmentClass}
                    data-testid="add-environment-class"
                    LeadingIcon={IconPlusSquare}
                    className="w-full rounded-xl border-[1px] border-dashed px-3 py-2 hover:bg-surface-02"
                    data-tracking-id="add-environment-class-environment-classes"
                >
                    <Text className="flex h-9 w-full items-center">Add a new environment class</Text>
                </Button>
            )}
            {isManagedRunner && (
                <Banner
                    variant="info"
                    className="mt-2"
                    text={
                        <div>
                            <div className="font-medium">Environment classes are managed by Ona Cloud</div>
                            <div className="text-sm">
                                Custom environment classes are available with Enterprise tier.{" "}
                                <a
                                    href="https://ona.com/docs/ona/runners/ona-cloud"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:no-underline"
                                >
                                    Learn more
                                </a>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
};

const EnvironmentClassRow: FC<{
    environmentClass: EnvironmentClass;
    readOnly: boolean;
    onEdit: (environmentClass: EnvironmentClass) => void;
    isManagedRunner: boolean;
}> = ({ environmentClass, readOnly, onEdit, isManagedRunner }) => {
    return (
        <div className="flex items-center justify-between gap-4 rounded-xl border-0.5 border-border-base bg-surface-primary px-3 py-2">
            <div className="flex items-center gap-2">
                <IconChip size="lg" />
                <div className="flex flex-col">
                    <Text className={cn("text-base font-bold", !environmentClass.enabled && "text-content-secondary")}>
                        <span>
                            {environmentClass.displayName} <span>{!environmentClass.enabled ? "(Disabled)" : ""}</span>
                        </span>
                    </Text>
                    <Text className={cn("text-sm", !environmentClass.enabled && "text-content-secondary")}>
                        {environmentClass.description}
                    </Text>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {environmentClass.enabled && !isManagedRunner && (
                    <Button
                        variant="secondary"
                        onClick={() => onEdit(environmentClass)}
                        disabled={readOnly}
                        data-tracking-id="edit-environment-class-environment-classes"
                    >
                        Edit
                    </Button>
                )}
                {!isManagedRunner && <EnvironmentClassSwitch environmentClass={environmentClass} disabled={readOnly} />}
            </div>
        </div>
    );
};
